import type { BannerData, ItemData } from "./Customization";

export type Pixel = {
    r: number;
    g: number;
    b: number;
    a?: number;
};

export async function colorize(
    img: HTMLImageElement,
    data: ItemData | BannerData,
    skinColor: string | undefined = undefined
): Promise<HTMLImageElement> {
    // If there is no given color style OR the color style is set, return the
    // image as is
    if (!("colorStyle" in data) || data.colorStyle === "set") return img;
    // If the layer is a skin
    else if (data.colorStyle === "skin" && skinColor)
        return await tintImage(img, skinColor);
    // If the color customization information is missing
    else if (!data.colorMap && !data.defaultColorMap) return img;
    // If the layer uses color zones
    else if (data.colorStyle === "zones" && data.colorZones)
        return await tintImageByZone(
            img,
            data.colorZones,
            data.colorMap
                ? data.colorMap
                : data.defaultColorMap
                ? data.defaultColorMap
                : []
        );
    // For all other layers, attempt to tint.
    else
        return await tintImage(
            img,
            data.colorMap
                ? data.colorMap[0]
                : data.defaultColorMap
                ? data.defaultColorMap[0]
                : ""
        );
}

export function intInRange(max = 1, min = 0): number {
    const range = max - min;
    const sel = Math.floor(Math.random() * range);
    return sel + min;
}

export function tintImage(
    image: HTMLImageElement,
    tintColor: string
): Promise<HTMLImageElement> {
    return new Promise((resolve) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = image.width;
        canvas.height = image.height;

        // If we can't get the context, return the original image.
        if (!ctx) return image;

        ctx.drawImage(image, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        const tint = hexToRGB(tintColor);

        for (let i = 0; i < data.length; i += 4) {
            // Skip fully transparent pixels
            if (data[i + 3] === 0) continue;

            data[i] = Math.floor((data[i] / 255) * tint.r); // Red
            data[i + 1] = Math.floor((data[i + 1] / 255) * tint.g); // Green
            data[i + 2] = Math.floor((data[i + 2] / 255) * tint.b); // Blue
            // Alpha (data[i + 3]) stays the same
        }

        ctx.putImageData(imageData, 0, 0);

        const outputImage = new Image();
        outputImage.onload = () => resolve(outputImage);
        outputImage.src = canvas.toDataURL();
        return outputImage;
    });
}

export function tintImageByZone(
    img: HTMLImageElement,
    colorZones: string[][],
    tintColors: string[]
): Promise<HTMLImageElement> {
    return new Promise((resolve) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;

        if (!ctx) return img;

        ctx.drawImage(img, 0, 0);

        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imgData.data;

        const zoneRGBs = colorZones.map((zone) => zone.map(hexToRGB));
        const tintRGBs = tintColors.map(hexToRGB);

        // Iterate through each pixel
        for (let i = 0; i < data.length; i += 4) {
            // Get the pixel date
            const pixel = {
                r: data[i],
                g: data[i + 1],
                b: data[i + 2],
                a: data[i + 3],
            };

            // If the pixel is transparent, skip it.
            if (pixel.a === 0) continue;

            // Iterate through each of the color zones
            for (let zoneIndex = 0; zoneIndex < zoneRGBs.length; zoneIndex++) {
                // Get all of the colors in this zone.
                const zone = zoneRGBs[zoneIndex];

                // Create a default color zone.
                const tint = { ...tintRGBs[zoneIndex] };

                if (zone.some((zoneColor) => colorMatch(pixel, zoneColor))) {
                    data[i] = Math.floor((pixel.r / 255) * tint.r);
                    data[i + 1] = Math.floor((pixel.g / 255) * tint.g);
                    data[i + 2] = Math.floor((pixel.b / 255) * tint.b);
                    break;
                }
            }
        }

        ctx.putImageData(imgData, 0, 0);

        const outputImage = new Image();
        outputImage.onload = () => resolve(outputImage);
        outputImage.src = canvas.toDataURL();
    });
}

export function hexToRGB(hex: string): Pixel {
    try {
        const num = parseInt(hex.replace("#", ""), 16);
        return {
            r: (num >> 16) & 255,
            g: (num >> 8) & 255,
            b: num & 255,
        };
    } catch (err) {
        console.log("Error parsing hex", hex, err);
        return { r: 0, g: 0, b: 0 };
    }
}

export function colorMatch(
    pixel: Pixel,
    target: Pixel,
    tolerance = 1
): boolean {
    return (
        Math.abs(pixel.r - target.r) <= tolerance &&
        Math.abs(pixel.g - target.g) <= tolerance &&
        Math.abs(pixel.b - target.b) <= tolerance
    );
}

export function clampHex(hex: string): string {
    // Return black by default
    if (!hex) return "#000000";

    // Remove #
    const clean = hex.replace(/[^a-fA-F0-9]/g, "").toUpperCase();

    // Take the first 6 characters, pad with 0 if too short
    const padded = clean.slice(0, 6).padEnd(6, "0");

    // Ensure it is exactly 6 characters
    return `#${padded}`;
}
